/**
 * IconBadge component for the Indigo design system.
 *
 * A rounded container that frames a design-system `Icon` on a soft background.
 * It is the small "app tile" affordance used to lead stat cards, promo banners,
 * empty states and review/task tiles.
 *
 * The tile holds a 20×20 icon; the container size defaults to 36×36 but can be
 * overridden via `size` (e.g. 40 for review tiles). Color is a semantic variant
 * pairing a soft background with an icon color — all from design tokens.
 *
 * @example
 * ```tsx
 * <IconBadge iconName="bonus" color="olive" />
 * <IconBadge iconName="pdf" color="subtle" size={40} />
 * ```
 */
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { CSSProperties, forwardRef, HTMLAttributes } from "react";
import { Icon, type IconProps } from "../Icons";
import "./IconBadge.css";

const iconBadgeVariants = cva("indigo-icon-badge", {
  variants: {
    color: {
      olive: "indigo-icon-badge--olive",
      amber: "indigo-icon-badge--amber",
      sky: "indigo-icon-badge--sky",
      rose: "indigo-icon-badge--rose",
      neutral: "indigo-icon-badge--neutral",
      subtle: "indigo-icon-badge--subtle",
    },
  },
  defaultVariants: {
    color: "olive",
  },
});

export interface IconBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof iconBadgeVariants> {
  /**
   * Name of the design-system icon to render inside the badge.
   */
  iconName: IconProps["name"];

  /**
   * Semantic color pairing (soft background + icon color).
   * `subtle` = canvas-shade background with a tertiary icon.
   * @default "olive"
   */
  color?: "olive" | "amber" | "sky" | "rose" | "neutral" | "subtle";

  /**
   * Container size in px (square). The inner icon stays 20×20.
   * @default 36
   */
  size?: number;

  /**
   * Additional CSS classes to apply to the badge container.
   */
  className?: string;
}

/**
 * IconBadge component
 *
 * Renders a square tinted tile (default 36×36) containing a 20×20 icon.
 */
export const IconBadge = forwardRef<HTMLSpanElement, IconBadgeProps>(
  (
    { iconName, color = "olive", size = 36, className, style, ...props },
    ref,
  ) => {
    const mergedStyle: CSSProperties = {
      width: `${size}px`,
      height: `${size}px`,
      ...(style ?? {}),
    };
    return (
      <span
        ref={ref}
        className={clsx(iconBadgeVariants({ color }), className)}
        style={mergedStyle}
        aria-hidden="true"
        {...props}
      >
        <Icon name={iconName} size="m" />
      </span>
    );
  },
);

IconBadge.displayName = "IconBadge";

export { iconBadgeVariants };
